import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/useAuth";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  LineChart,
  Users2,
  Database,
  ClipboardList,
  Calculator,
  FileSpreadsheet,
  Handshake,
  ArrowRight,
  Users,
} from "lucide-react";

const dashboardItems = [
  {
    id: "deal-analyzer",
    title: "Deal Analyzer",
    description:
      "Model any hotel deal in minutes. P&L projections, payroll planning, and investment returns.",
    icon: Calculator,
    iconBg: "bg-primary/10",
    iconColor: "text-primary",
    link: "/underwriting",
    badge: null,
    enabled: true,
  },
  {
    id: "playbook",
    title: "Hotel Opening Playbook",
    description:
      "Step-by-step guidance from site selection to launch day. 500+ tasks, zero guesswork.",
    icon: ClipboardList,
    iconBg: "bg-amber-500/10",
    iconColor: "text-amber-600",
    link: "/roadmap",
    enabled: true,
  },
  {
    id: "templates",
    title: "Templates",
    description:
      "Download ready-to-use Excel models, PowerPoint decks, and operational documents.",
    icon: FileSpreadsheet,
    iconBg: "bg-emerald-500/10",
    iconColor: "text-emerald-600",
    link: "/templates",
    badge: null,
    enabled: true,
  },

  {
    id: "vetted-partners",
    title: "Vetted Partners",
    description:
      "Pre-screened architects, designers, consultants, and technology vendors.",
    icon: Handshake,
    iconBg: "bg-sky-500/10",
    iconColor: "text-sky-600",
    link: "/partners",
    badge: null,
    enabled: true,
  },

  {
    id: "consultancy",
    title: "Consultancy",
    description:
      "Get expert help validating assumptions and reviewing your deal.",
    icon: Users2,
    iconBg: "bg-indigo-500/10",
    iconColor: "text-indigo-600",
    link: "/consultancy",
    badge: null,
    enabled: true,
  },
  {
    id: "benchmarks",
    title: "Benchmarks",
    description:
      "Market benchmarks for ADR, occupancy, payroll, and opex (Balearics first).",
    icon: Database,
    iconBg: "bg-purple-500/10",
    iconColor: "text-purple-600",
    link: null,
    badge: "Coming soon",
    enabled: false,
  },
  {
    id: "founders-circle",
    title: "Founders Circle",
    description:
      "Connect with other boutique hotel founders. Share learnings, find partners, grow together.",
    icon: Users,
    iconBg: "bg-violet-500/10",
    iconColor: "text-violet-600",
    link: null,
    badge: "Coming soon",
    enabled: false,
  },
];

export default function DashboardPage() {
  const { user, displayName } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <main className="container py-12">
        <div className="mb-10">
          <h1 className="text-3xl font-bold tracking-tight mb-2">
            {`Welcome back${displayName ? `, ${displayName}` : ""}.`}
          </h1>
          <p className="text-muted-foreground text-lg">
            Ready to move your hotel idea forward?
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {dashboardItems.map((item) => (
            <Card
              key={item.id}
              className={`relative transition-all duration-200 ${
                item.enabled
                  ? "hover:shadow-lg hover:border-primary/20 cursor-pointer"
                  : "opacity-70"
              }`}
              onClick={() => item.enabled && item.link && navigate(item.link)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className={`p-3 rounded-xl ${item.iconBg}`}>
                    <item.icon className={`h-6 w-6 ${item.iconColor}`} />
                  </div>
                  {item.badge && (
                    <Badge variant="secondary" className="text-xs">
                      {item.badge}
                    </Badge>
                  )}
                </div>
                <CardTitle className="text-xl mt-4">{item.title}</CardTitle>
                <CardDescription className="text-sm leading-relaxed">
                  {item.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {item.enabled ? (
                  <Button
                    variant="link"
                    className="p-0 h-auto text-primary-blue"
                  >
                    Open {item.title} <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                ) : (
                  <span className="text-sm text-muted-foreground">
                    Coming soon
                  </span>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-16 text-center">
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
            Hotel Foundry provides tools and estimates for planning purposes
            only. All projections should be validated with qualified
            professionals before making investment decisions.
          </p>
        </div>
      </main>
    </div>
  );
}
